import { useSyncExternalStore } from 'react';

/**
 * In-memory session store. The access token never touches localStorage; the refresh token is an
 * httpOnly cookie managed by the API, so a page reload restores the session via silent refresh.
 */
export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';

export interface SessionState {
  status: SessionStatus;
  accessToken: string | null;
  /** epoch ms when the access token expires */
  expiresAt: number | null;
  /**
   * Set while a Super Admin is inside a tenant via a read-only support session. The platform session
   * stays recoverable through its refresh cookie; ending support refreshes back into it.
   */
  support: SupportSessionState | null;
}

export interface SupportSessionState {
  tenantId: string;
}

const ANONYMOUS: SessionState = { status: 'anonymous', accessToken: null, expiresAt: null, support: null };
const UNKNOWN: SessionState = { status: 'unknown', accessToken: null, expiresAt: null, support: null };

let state: SessionState = UNKNOWN;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export const sessionStore = {
  getState(): SessionState {
    return state;
  },
  getAccessToken(): string | null {
    return state.accessToken;
  },
  /** Stores a normal (non-support) access token. Ends any support session. */
  setToken(accessToken: string, expiresInSeconds: number): void {
    state = {
      status: 'authenticated',
      accessToken,
      expiresAt: Date.now() + expiresInSeconds * 1000,
      support: null,
    };
    emit();
  },
  /** Swaps the in-memory token for a read-only support token into a tenant. */
  beginSupport(accessToken: string, expiresInSeconds: number, tenantId: string): void {
    state = {
      status: 'authenticated',
      accessToken,
      expiresAt: Date.now() + expiresInSeconds * 1000,
      support: { tenantId },
    };
    emit();
  },
  getSupport(): SupportSessionState | null {
    return state.support;
  },
  clear(): void {
    state = ANONYMOUS;
    emit();
  },
  /** test helper */
  reset(): void {
    state = UNKNOWN;
    emit();
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useSession(): SessionState {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.getState, sessionStore.getState);
}
